// ── periodLabel — sport-specific composition of Unabated period state
//
// Hydra ships `periodLabel` (long form, e.g. "Ninth Inning", "First
// Quarter") + `gameClock` (sport-specific extra: MLB "T"/"B"/"E" half-
// inning, basketball minutes:seconds, etc.). This helper composes the
// pair into a short chrome-friendly status string:
//
//   MLB live  → "Top 3rd" / "Bot 9th" / "End 12th"
//   (others)  → the raw periodLabel (verbose), pending per-sport
//               abbreviation patterns
//
// Used by:
//   • src/views/GameDetail/GameScoreHeader.tsx — the LIVE pill body
//   • src/views/ValueDashboard/GameTable.tsx — the date column's
//     second line on live games (replaces the scheduled event start time)
//
// Returns null when nothing useful can be composed (pre-game with no
// state, missing fields, etc.); callers fall back to their existing
// scheduled-time / countdown UI.

export interface PeriodStateInput {
  periodLabel?: string | null;
  gameClock?: string | null;
}

/** True only for a Unabated FINAL (statusId 3) that carries the score.
 *  A scoreless "final" is a stale/garbage gameState record — live soccer
 *  hit this 2026-06-12: a May 20 record reported a game in play as final
 *  with null scores, which blanked the Odds column, archived the
 *  GameDetail page, and showed a scoreless FINAL banner. A real final
 *  always has the score; callers must fall back to their live-store
 *  signals when this returns false. */
export function isConfirmedFinal(
  gs:
    | { statusId?: number | null; awayScore?: unknown; homeScore?: unknown }
    | null
    | undefined,
): boolean {
  return (
    gs?.statusId === 3 &&
    typeof gs.awayScore === 'number' &&
    typeof gs.homeScore === 'number'
  );
}

export function formatGameStatus(
  gs: PeriodStateInput | null,
  sport: string | null | undefined,
): string | null {
  if (!gs) return null;
  const label = typeof gs.periodLabel === 'string' ? gs.periodLabel : null;
  const clock = typeof gs.gameClock === 'string' ? gs.gameClock : '';
  if (!label && !clock) return null;

  const normalizedSport = (sport ?? '').toUpperCase();
  if (normalizedSport === 'MLB' && label) {
    const inning = abbreviateInningLabel(label);
    if (inning) {
      const half =
        clock === 'T'
          ? 'Top'
          : clock === 'B'
            ? 'Bot'
            : clock === 'E'
              ? 'End'
              : null;
      return half ? `${half} ${inning}` : inning;
    }
  }

  return label || clock || null;
}

// "Ninth Inning" → "9th". Handles 1-20 (covers extra-innings comfortably);
// anything we don't recognize falls through to the raw label.
function abbreviateInningLabel(label: string): string | null {
  const m = label.match(/^(\w+)\s+Inning$/i);
  if (!m) return null;
  const word = m[1].toLowerCase();
  const wordToNum: Record<string, number> = {
    first: 1, second: 2, third: 3, fourth: 4, fifth: 5,
    sixth: 6, seventh: 7, eighth: 8, ninth: 9, tenth: 10,
    eleventh: 11, twelfth: 12, thirteenth: 13, fourteenth: 14,
    fifteenth: 15, sixteenth: 16, seventeenth: 17, eighteenth: 18,
    nineteenth: 19, twentieth: 20,
  };
  const n = wordToNum[word];
  if (!n) return null;
  return ordinal(n);
}

function ordinal(n: number): string {
  const v = n % 100;
  if (v >= 11 && v <= 13) return `${n}th`;
  const last = n % 10;
  if (last === 1) return `${n}st`;
  if (last === 2) return `${n}nd`;
  if (last === 3) return `${n}rd`;
  return `${n}th`;
}
